import React, { useEffect } from 'react';
import './LoginPopup.css';
import { useDispatch } from 'react-redux';
import { GoogleAuthProvider, signInWithPopup, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase/firebaseConfig';
import { setIsLoggedIn, setUser } from '../../reducers/userReducer';
import facebookLogo from '../../assets/images/facebook.svg';

interface LoginPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const LoginPopup: React.FC<LoginPopupProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();

  // Check for existing user session on component mount
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is signed in
        dispatch(setUser({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
        }));
        dispatch(setIsLoggedIn(true));
        
        // Store user data in localStorage for persistence
        localStorage.setItem('user_data', JSON.stringify({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
        }));
      }
    });
    
    // Check if we have user data in localStorage
    const storedUserData = localStorage.getItem('user_data');
    if (storedUserData) {
      const userData = JSON.parse(storedUserData);
      dispatch(setUser(userData));
      dispatch(setIsLoggedIn(true));
    }
    
    return () => unsubscribe();
  }, [dispatch]);

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider) as any;
      const userData = {
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
      };
      
      dispatch(setUser(userData));
      dispatch(setIsLoggedIn(true));
      
      // Store user data in localStorage for persistence
      localStorage.setItem('access_token_fb_auth', result.user.accessToken);
      localStorage.setItem('user_data', JSON.stringify(userData));
      
      onClose();
    } catch (error) {
      console.error("Error logging in with Google: ", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="login-popup-overlay">
      <div className="login-popup-container">
        <button className="login-close-button" onClick={onClose}>×</button>
        
        <div className="login-popup-content">
          <h1 className="login-popup-title">Login</h1>
          
          <div className="login-options">
            <button 
              className="login-option-button"
              onClick={handleGoogleLogin}
            >
              <img 
                src="https://developers.google.com/identity/images/g-logo.png" 
                alt="Google" 
                className="login-option-icon" 
              />
              <span>Continue with Google</span>
            </button>
            
            <button 
              className="login-option-button disabled"
              disabled
            >
              <img src={facebookLogo} alt="Facebook" className="login-option-icon" />
              <span>Continue with Facebook</span>
            </button>
            
            <button 
              className="login-option-button disabled"
              disabled
            >
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg" 
                alt="Apple" 
                className="login-option-icon" 
              />
              <span>Continue with Apple</span>
            </button>
          </div>
          
          <div className="login-terms">
            By continuing, you accept our <a href="/terms-of-use" className="login-terms-link">Terms of Service</a> & <a href="/privacy-policy" className="login-terms-link">Privacy Policy</a>.
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPopup;
